import {
  PapiAdoptable,
  PapiDeviation,
  PapiNcBppModule,
  PapiNcComment,
  PapiNcMessageCorrespondence,
} from '@wix/da-papi-types';
import { Chance } from 'chance';
import { users } from '../users';
import { deviations } from '../deviations';
import { pickone } from '../helpers';
import { makeNCNotification } from '../fakes/notification';
import { commentsByType } from '../comments';

const chance = new Chance();

const commentables: PapiNcComment['commentable'][] = [
  { dataKey: 'deviation', deviation: pickone(deviations) },
  { dataKey: 'profile', profile: pickone(users) },
];

export function makeNcComment(): PapiNcComment {
  const comment = chance.pickone(commentsByType.draft);
  return {
    commentId: 1,
    typeId: 1,
    itemId: 1,
    parentId: 2,
    posted: new Date().toISOString(),
    textContent: comment.textContent,
    user: pickone(users),
    commentUrl: 'https://www.google.com',
    commentable: pickone(commentables),
    replies: 1,
    likes: 2,
  };
}

export function makeNcCorrespondenceNotification(name: string, data: PapiNcMessageCorrespondence) {
  return makeNCNotification(name, {
    messageData: {
      dataKey: 'correspondence',
      correspondence: data,
    },
  });
}

export function makeNcCorrespondenceData(
  status: ['voting'] | ['decided', number] | ['withdrawn'] | ['expired'],
  bppModule: PapiNcBppModule,
  {
    showCounts = false,
    canWithdraw = false,
    hasVoted = undefined,
    numVotesNeeded = 0,
    choices = [],
  }: Partial<PapiNcMessageCorrespondence> = {}
): PapiNcMessageCorrespondence {
  return {
    status: status[0],
    canVote: !hasVoted && status[0] === 'voting',
    canWithdraw,
    numVotesNeeded,
    showCounts,
    result: status[0] === 'decided' ? status[1] : null,
    choices,
    hasVoted,
    bppModule,
    comment: {
      count: chance.integer({ min: 0, max: 10 }),
      url: 'https://www.deviantart.com',
    },
  };
}

export function makeNcGroupVotingCorrespondenceData(
  status: ['voting'] | ['decided', number] | ['withdrawn'] | ['expired'],
  bppModule: PapiNcBppModule
) {
  const hasVoted = status[0] === 'voting' ? false : chance.bool();
  return makeNcCorrespondenceData(status, bppModule, {
    showCounts: true,
    hasVoted: hasVoted
      ? {
          choice: chance.integer({ min: 1, max: 2 }),
          ts: new Date().toISOString(),
        }
      : undefined,
    numVotesNeeded: chance.integer({ min: 3, max: 5 }),
    choices: [
      { label: 'yes', value: 1, votesCast: chance.integer({ min: 0, max: 3 }) },
      { label: 'no', value: 2, votesCast: chance.integer({ min: 0, max: 3 }) },
    ],
  });
}

export function makeNcGroupVotingCorrespondenceNotificationSet(
  name: string,
  data: PapiNcBppModule
) {
  return {
    voting: makeNcCorrespondenceNotification(
      name,
      makeNcGroupVotingCorrespondenceData(['voting'], data)
    ),
    decided_yes: makeNcCorrespondenceNotification(
      name,
      makeNcGroupVotingCorrespondenceData(['decided', 1], data)
    ),
    decided_no: makeNcCorrespondenceNotification(
      name,
      makeNcGroupVotingCorrespondenceData(['decided', 2], data)
    ),
    expired: makeNcCorrespondenceNotification(
      name,
      makeNcGroupVotingCorrespondenceData(['expired'], data)
    ),
    withdrawn: makeNcCorrespondenceNotification(
      name,
      makeNcGroupVotingCorrespondenceData(['withdrawn'], data)
    ),
  };
}

export function makeNcModerationReportData(
  caseType: 'mature' | 'underage' | 'policy' | 'copyright' | 'pornography' | 'monetized_ai',
  caseStatus: 'pending' | 'resolved' | 'disputed' | 'dispute_rejected' | 'dispute_accepted',
  {
    taggedByOwner = false,
    mrfRequest = false,
    sprfRequest = false,
    matureDisputedAndAccepted = false,
    moreInfoProvided = false,
  } = {}
) {
  return makeNcCorrespondenceData(['voting'], {
    dataKey: 'moderation_report',
    moderationReport: {
      deviationId: chance.integer(),
      deviation: pickone(deviations),
      caseId: chance.integer(),
      caseStatus,
      caseType,
      editUrl: '',
      title: 'This is deviation title',
      publicComment: '',
      taggedByOwner,
      mrfRequest,
      sprfRequest,
      matureDisputedAndAccepted,
      moreInfoProvided,
    },
  });
}

export function extendAdoptable(
  deviation: PapiDeviation,
  overrideAdopt: Partial<NonNullable<PapiDeviation['extended']>['adoptable']> = {},
  overrideOffers: Partial<PapiAdoptable['offers']> | null = undefined,
  overrideOwnOffer = {},
  overrideStatus: NonNullable<PapiDeviation['adoptable']>['status'] | undefined = undefined
): PapiDeviation {
  const dev: PapiDeviation = deviation;

  const toDate = new Date();
  toDate.setDate(toDate.getDate() + 2);

  return {
    ...dev,
    isPurchasable: false, // without PCP
    isAdoptable: true,
    adoptable: {
      status: overrideStatus || 'onsale',
      fixedPrice: overrideAdopt
        ? overrideAdopt.fixedPrice
        : {
            fixedPrice: 99,
          },
      offers:
        overrideOffers === null
          ? undefined
          : {
              offerPrice: overrideOffers?.minOffer ?? 10,
              numOffers: overrideOffers?.numOffers ?? 8,
              offerEndTime: overrideOffers?.offerEndTime ?? toDate.toISOString(),
            },
    },
    extended: {
      adoptable: {
        creator: dev.author!,
        owner: dev.author!,
        assetSourceUrl: 'https://foo.com',
        deviationId: dev.deviationId,
        fixedPrice: {
          fixedPrice: overrideAdopt.fixedPrice?.fixedPrice ?? 99,
          subproductId: 1,
        },
        isCreator: false,
        isOwner: false,
        offers:
          overrideOffers === null
            ? undefined
            : (overrideOffers ?? {
                highestOffer: 66,
                minOffer: 10,
                numOffers: 45,
                offerEndTime: toDate.toISOString(),
                ownOffer: {
                  dollarPrice: 30,
                  isAnonymous: 1,
                  offerId: 1,
                  status: 'created',
                  ts: '',
                  from: chance.pickone(users),
                  to: dev.author!,
                  ...overrideOwnOffer,
                },
              }),
        ...overrideAdopt,
      },
    },
  };
}
